import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import ChatPage from "@/pages/ChatPage";
import BookingsPage from "@/pages/BookingsPage";
import PrepBoard from "@/pages/PrepBoard";
import PracticalsPage from "@/pages/PracticalsPage";
import TimetablePage from "@/pages/TimetablePage";
import RoomsPage from "@/pages/RoomsPage";
import InventoryPage from "@/pages/InventoryPage";
import AdminPage from "@/pages/AdminPage";
import ToBuyPage from "@/pages/ToBuyPage";
import SupplierFinderPage from "@/pages/SupplierFinderPage";
import DocumentsPage from "@/pages/DocumentsPage";
import SignupPage from "@/pages/SignupPage";
import SuperAdminPage from "@/pages/SuperAdminPage";
import { ForgotPasswordPage, ResetPasswordPage } from "@/pages/PasswordPages";
import { LockedScreen } from "@/components/Billing";
import AnnualRemindersPage from "@/pages/AnnualRemindersPage";
import TechnicianTasksPage from "@/pages/TechnicianTasksPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import BorrowedEquipmentPage from "@/pages/BorrowedEquipmentPage";
import PrepDocumentsPage from "@/pages/PrepDocumentsPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import FaqPage from "@/pages/FaqPage";
import { PwaInstallPrompt } from "@/components/PwaInstallPrompt";

function Protected({ children, perm, superOnly }) {
  const { user, can } = useAuth();
  if (user === null) {
    return (
      <div
        className="h-screen grid place-items-center"
        data-testid="auth-loading"
      >
        <div className="dot-pulse flex gap-1.5">
          <span />
          <span />
          <span />
        </div>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  if (user.billing?.locked && !user.superadmin) return <LockedScreen />;
  if (superOnly && !user.superadmin) return <Navigate to="/" replace />;
  if (perm && !can(perm)) return <Navigate to="/" replace />;
  return <AppShell>{children}</AppShell>;
}

function Home() {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.billing?.locked && !user.superadmin)
    return <Navigate to="/chat" replace />;
  return (
    <Navigate to={user.role === "technician" ? "/prep" : "/chat"} replace />
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route
        path="/super"
        element={
          <Protected superOnly>
            <SuperAdminPage />
          </Protected>
        }
      />
      <Route path="/" element={<Home />} />
      <Route
        path="/chat"
        element={
          <Protected perm="chat">
            <ChatPage />
          </Protected>
        }
      />
      <Route
        path="/chat/:cid"
        element={
          <Protected perm="chat">
            <ChatPage />
          </Protected>
        }
      />
      <Route
        path="/bookings"
        element={
          <Protected perm="booking:read_own">
            <BookingsPage />
          </Protected>
        }
      />
      <Route
        path="/prep"
        element={
          <Protected perm="booking:prep">
            <PrepBoard />
          </Protected>
        }
      />
      <Route
        path="/practicals"
        element={
          <Protected perm="practical:read">
            <PracticalsPage />
          </Protected>
        }
      />
      <Route
        path="/timetable"
        element={
          <Protected perm="timetable:read_own">
            <TimetablePage />
          </Protected>
        }
      />
      <Route
        path="/rooms"
        element={
          <Protected perm="booking:prep">
            <RoomsPage />
          </Protected>
        }
      />
      <Route
        path="/inventory"
        element={
          <Protected perm="inventory:read">
            <InventoryPage />
          </Protected>
        }
      />
      <Route
        path="/tobuy"
        element={
          <Protected perm="tobuy:manage">
            <ToBuyPage />
          </Protected>
        }
      />
      <Route
        path="/suppliers"
        element={
          <Protected perm="suppliers:read">
            <SupplierFinderPage />
          </Protected>
        }
      />
      <Route
        path="/documents"
        element={
          <Protected perm="documents:read">
            <DocumentsPage />
          </Protected>
        }
      />
      <Route path="/prep-documents" element={<Protected perm="documents:manage"><PrepDocumentsPage /></Protected>} />
      <Route
        path="/annual-reminders"
        element={
          <Protected perm="annual_reminders:manage">
            <AnnualRemindersPage />
          </Protected>
        }
      />
      <Route
        path="/my-todos"
        element={
          <Protected perm="technician_tasks:manage">
            <TechnicianTasksPage />
          </Protected>
        }
      />
      <Route
        path="/borrowed-equipment"
        element={
          <Protected perm="borrowed:manage">
            <BorrowedEquipmentPage />
          </Protected>
        }
      />
      <Route
        path="/admin"
        element={
          <Protected perm="users:manage">
            <AdminPage />
          </Protected>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <PwaInstallPrompt />
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}
